import type { ProjectMember } from '@/features/projects/hooks/useAssigneeDirectory';
import type { ProjectNode } from '@/shared/types/projectData';
import { getNodeAssignees, isAgentAssigneeId, normalizeAssigneeValue } from '@/shared/utils/projectDataUtils';

type MaybeNode = Partial<ProjectNode> & { id?: unknown; parentId?: unknown; projectId?: unknown };

export const EXTERNAL_AGENT_REDACTION_ROLES = new Set(['client', 'partner']);

export const shouldRedactAgentAssigneesForRole = (role?: string | null): boolean =>
  EXTERNAL_AGENT_REDACTION_ROLES.has(String(role || '').toLowerCase());

export const firstHumanAssignee = (node?: MaybeNode | null): string | null => {
  if (!node) return null;

  for (const assignee of getNodeAssignees(node as ProjectNode)) {
    const normalized = normalizeAssigneeValue(assignee);
    if (normalized && !isAgentAssigneeId(normalized)) {
      return normalized;
    }
  }

  return null;
};

export const getProjectOwnerMemberId = (members?: ProjectMember[] | null): string | null => {
  const owner = (members || []).find((member) => {
    const role = String(member.projectRole || member.role || '').toLowerCase();
    return role === 'owner' && member.assignmentEligible !== false && !isAgentAssigneeId(member.id);
  });
  return owner?.id || null;
};

interface ResolveFallbackOptions {
  node?: MaybeNode | null;
  nodes?: MaybeNode[];
  projectAssigneeId?: string | null;
  projectMembers?: ProjectMember[];
}

export const resolveExternalAgentAssigneeFallback = ({
  node,
  nodes = [],
  projectAssigneeId,
  projectMembers,
}: ResolveFallbackOptions): string | null => {
  const byId = new Map<string, MaybeNode>();
  nodes.forEach((candidate) => {
    if (typeof candidate.id === 'string') {
      byId.set(candidate.id, candidate);
    }
  });

  const visited = new Set<string>();
  let parentId = typeof node?.parentId === 'string' ? node.parentId : null;
  while (parentId && !visited.has(parentId)) {
    visited.add(parentId);
    const parent = byId.get(parentId);
    const parentAssignee = firstHumanAssignee(parent);
    if (parentAssignee) {
      return parentAssignee;
    }
    parentId = typeof parent?.parentId === 'string' ? parent.parentId : null;
  }

  const normalizedProjectAssignee = normalizeAssigneeValue(projectAssigneeId);
  if (normalizedProjectAssignee && !isAgentAssigneeId(normalizedProjectAssignee)) {
    return normalizedProjectAssignee;
  }

  return getProjectOwnerMemberId(projectMembers);
};

interface ResolveDisplayOptions extends ResolveFallbackOptions {
  shouldRedact: boolean;
}

export const resolveDisplayAssigneeForViewer = (
  assigneeId: string | null | undefined,
  options: ResolveDisplayOptions,
): string | null => {
  const normalized = normalizeAssigneeValue(assigneeId);
  if (!normalized) return null;
  if (!options.shouldRedact || !isAgentAssigneeId(normalized)) return normalized;
  return resolveExternalAgentAssigneeFallback(options);
};
